import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { v4 as uuidV4 } from 'uuid'
import EventSource, { EventSourceInitDict } from 'eventsource'
import { Dict } from '@oneweb/reach-me-lib/src/typeScriptHelpers'
import { getOneWebWKOs, jwe } from '../oneweb'
import { getEnvironmentConfig, getOpenEventSource, getRegionFromCountry } from '../utils'

type NeededRequestConfigKeys = 'url' | 'method' | 'baseURL' | 'headers' | 'params' | 'data' | 'responseType'

export interface ApiFetchPayload extends Pick<AxiosRequestConfig, NeededRequestConfigKeys> {
  url: string
  name?: string
  defaultHeaders?: Dict
}

interface ApiStreamPayload extends ApiFetchPayload {
  name: string
}

export interface ApiInterface {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetch: (config: ApiFetchPayload) => Promise<any>
}

// axios default configs
axios.defaults.headers.common['Content-Type'] = 'application/json'

type ApiConfig = {
  headers: Dict
  endpoint: string
}
export let apiConfig: ApiConfig | null = null

export const apiFetch = async ({
  url,
  method,
  params,
  data,
  headers,
  responseType = 'json',
}: ApiFetchPayload): Promise<AxiosResponse> => {
  if (!apiConfig) {
    await initializeApiConfig()
  }
  const requestParams = {
    url,
    method,
    headers: {
      'Tracking-ID': uuidV4(),
      Authorization: `Bearer ${jwe}`,
      ...headers,
    },
    params,
    data,
    responseType,
  }
  return axios.request(requestParams)
}

export const apiStream = async ({ name, url, params, headers }: ApiStreamPayload): Promise<EventSource> => {
  !!apiConfig || (await initializeApiConfig())
  let fullURL = apiConfig ? apiConfig.endpoint + url : ''
  const searchParams = new URLSearchParams(params)
  fullURL = searchParams.toString() ? `${fullURL}?${searchParams.toString()}` : fullURL

  const options: EventSourceInitDict = {
    headers: {
      ...(apiConfig ? apiConfig.headers : null),
      'Tracking-ID': uuidV4(),
      Authorization: `Bearer ${jwe}`,
      ...headers,
    },
  }
  return getOpenEventSource(name) ?? new EventSource(fullURL, options)
}

const configureAPI = (headers: Dict, baseURL: string): void => {
  axios.defaults.headers.common = {
    ...axios.defaults.headers.common,
    ...headers,
  }
  axios.defaults.baseURL = baseURL

  apiConfig = {
    headers,
    endpoint: baseURL,
  }
}

const DEFAULT_COUNTRY = 'PT'
export const initializeApiConfig = async (): Promise<void> => {
  const state = await getOneWebWKOs()
  const config = await getEnvironmentConfig(state)
  const country = state.Locale?.country || DEFAULT_COUNTRY
  configureAPI(
    {
      'X-Token-Type': 'OWE',
      'x-api-key': config.api.apiKey,
    },
    config.api.endpointsBaseURL[await getRegionFromCountry(country)],
  )
}
